/**
 * components/procurement/ContractDrawer.jsx — left-side drawer to create or
 * edit a contract in its simplest form (per spec: "form opens through a modal
 * drawer from left").
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Save } from 'lucide-react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter,
} from '../ui/sheet';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../ui/select';
import { SiteLinkPicker } from './SiteLinkPicker';
import { COMMODITIES, CONTRACT_TYPES, createContract, patchContract } from '../../api/contracts';

const EMPTY = {
  name: '', commodity: '', contract_type: '', vendor_name: '', contract_owner: '',
  start_date: '', end_date: '', issue_date: '',
  company_name: '', ein: '', utility_account: '', company_address: '',
  contract_group: '', td_tariff: '',
  linked_property_ids: [], linked_account_ids: [], linked_meter_ids: [],
};

const Field = ({ label, children, required }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
      {label}{required && <span className="text-destructive ml-0.5">*</span>}
    </Label>
    {children}
  </div>
);

export function ContractDrawer({ open, onOpenChange, accountId, existing, onSaved }) {
  const isEdit = !!existing;
  const [form, setForm] = useState(EMPTY);
  const [labels, setLabels] = useState({ property: {}, account: {}, meter: {} });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (existing) {
        setForm({ ...EMPTY, ...existing,
          linked_property_ids: existing.linked_property_ids || [],
          linked_account_ids: existing.linked_account_ids || [],
          linked_meter_ids: existing.linked_meter_ids || [],
        });
        const links = existing.links || {};
        setLabels({
          property: Object.fromEntries((links.properties || []).map((p) => [p.id, `${p.name || ''}${p.city ? ` · ${p.city}` : ''}`])),
          account: Object.fromEntries((links.accounts || []).map((a) => [a.id, `${a.account_number || ''} · ${a.vendor_name || ''}`])),
          meter: Object.fromEntries((links.meters || []).map((m) => [m.id, `${m.meter_number || ''}`])),
        });
      } else {
        setForm(EMPTY);
        setLabels({ property: {}, account: {}, meter: {} });
      }
    }
  }, [open, existing]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error('Contract name is required');
    if (!form.commodity) return toast.error('Commodity is required');
    if (!form.contract_type) return toast.error('Contract type is required');
    setSaving(true);
    try {
      if (isEdit) {
        await patchContract({ accountId, id: existing.id, patch: form });
        toast.success('Contract updated');
      } else {
        await createContract({ accountId, payload: form });
        toast.success('Contract created');
      }
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error('Save failed', { description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="left" className="w-full sm:max-w-xl overflow-y-auto" data-testid="contract-drawer">
        <SheetHeader>
          <SheetTitle>{isEdit ? 'Edit Contract' : 'New Contract'}</SheetTitle>
          <SheetDescription>
            {isEdit ? 'Update contract details.' : 'Create a contract in its simplest form. Pricing per type is configured on the detail page.'}
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-5 py-4">
          <Field label="Contract Name" required>
            <Input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g. Oncor Electric Supply 2025" data-testid="contract-name-input" />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Commodity" required>
              <Select value={form.commodity} onValueChange={(v) => set('commodity', v)}>
                <SelectTrigger data-testid="contract-commodity-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {COMMODITIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Contract Type" required>
              <Select value={form.contract_type} onValueChange={(v) => set('contract_type', v)}>
                <SelectTrigger data-testid="contract-type-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {CONTRACT_TYPES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Vendor / Supplier">
              <Input value={form.vendor_name} onChange={(e) => set('vendor_name', e.target.value)} placeholder="e.g. IGS Energy" data-testid="contract-vendor-input" />
            </Field>
            <Field label="Contract Owner">
              <Input value={form.contract_owner} onChange={(e) => set('contract_owner', e.target.value)} placeholder="Owner name" data-testid="contract-owner-input" />
            </Field>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <Field label="Start Date">
              <Input type="date" value={form.start_date || ''} onChange={(e) => set('start_date', e.target.value)} data-testid="contract-start-input" />
            </Field>
            <Field label="End Date">
              <Input type="date" value={form.end_date || ''} onChange={(e) => set('end_date', e.target.value)} data-testid="contract-end-input" />
            </Field>
            <Field label="Issue / Award Date">
              <Input type="date" value={form.issue_date || ''} onChange={(e) => set('issue_date', e.target.value)} data-testid="contract-issue-input" />
            </Field>
          </div>

          <details className="border rounded-md p-3" open={isEdit}>
            <summary className="cursor-pointer text-sm font-medium">Supplier / Company details</summary>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <Field label="Company Name"><Input value={form.company_name || ''} onChange={(e) => set('company_name', e.target.value)} data-testid="contract-company-input" /></Field>
              <Field label="EIN"><Input value={form.ein || ''} onChange={(e) => set('ein', e.target.value)} data-testid="contract-ein-input" /></Field>
              <Field label="Utility Account"><Input value={form.utility_account || ''} onChange={(e) => set('utility_account', e.target.value)} /></Field>
              <Field label="Contract Group"><Input value={form.contract_group || ''} onChange={(e) => set('contract_group', e.target.value)} /></Field>
              <Field label="T&D Tariff"><Input value={form.td_tariff || ''} onChange={(e) => set('td_tariff', e.target.value)} /></Field>
              <Field label="Address"><Input value={form.company_address || ''} onChange={(e) => set('company_address', e.target.value)} /></Field>
            </div>
          </details>

          <div className="space-y-4 border-t pt-4">
            <div className="text-sm font-semibold">Contracted Sites</div>
            <SiteLinkPicker accountId={accountId} kind="property" commodity={form.commodity}
              selected={form.linked_property_ids} selectedLabels={labels.property}
              onChange={(ids) => set('linked_property_ids', ids)} />
            <SiteLinkPicker accountId={accountId} kind="account" commodity={form.commodity}
              selected={form.linked_account_ids} selectedLabels={labels.account}
              onChange={(ids) => set('linked_account_ids', ids)} />
            <SiteLinkPicker accountId={accountId} kind="meter" commodity={form.commodity}
              selected={form.linked_meter_ids} selectedLabels={labels.meter}
              onChange={(ids) => set('linked_meter_ids', ids)} />
          </div>
        </div>

        <SheetFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="contract-drawer-cancel">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="contract-drawer-save">
            <Save className="w-4 h-4 mr-1.5" />{saving ? 'Saving…' : (isEdit ? 'Update Contract' : 'Create Contract')}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

export default ContractDrawer;
